import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useComparisonMutations } from "@/hooks/useComparisonMutations";
import { Loader2 } from "lucide-react";
import { EstimateLineItem } from "@/api/generated/client/models/EstimateLineItem";
import type { CreateLineItemRequest } from "@/api/generated/client/models/CreateLineItemRequest";

interface CreateLineItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    roomId: number;
    comparisonId: number;
    canWrite: boolean;
    initialData?: EstimateLineItem;
}

export function CreateLineItemModal({
    isOpen,
    onClose,
    roomId,
    comparisonId,
    canWrite,
    initialData
}: CreateLineItemModalProps) {
    const { createLineItem, updateLineItem } = useComparisonMutations(comparisonId, canWrite);

    // Form State
    const [description, setDescription] = useState('');
    const [quantity, setQuantity] = useState<string>('');
    const [unit, setUnit] = useState('');
    const [unitPrice, setUnitPrice] = useState<string>('');
    const [tax, setTax] = useState<string>('');
    const [op, setOp] = useState<string>('');
    const [rcv, setRcv] = useState<string>('');
    const [lineItemNumber, setLineItemNumber] = useState<string>('');

    // Load initial data
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setDescription(initialData.description);
                setQuantity(initialData.quantity?.toString() || '');
                setUnit(initialData.unit || '');
                setUnitPrice(initialData.unitPriceCents ? (initialData.unitPriceCents / 100).toFixed(2) : '');
                setTax(initialData.taxCents ? (initialData.taxCents / 100).toFixed(2) : '');
                setOp(initialData.opCents ? (initialData.opCents / 100).toFixed(2) : '');
                setRcv(initialData.rcvCents ? (initialData.rcvCents / 100).toFixed(2) : '');
                setLineItemNumber(initialData.lineItemNumber || '');
            } else {
                // Reset for creation
                setDescription('');
                setQuantity('');
                setUnit('');
                setUnitPrice('');
                setTax('');
                setOp('');
                setRcv('');
                setLineItemNumber('');
            }
        }
    }, [isOpen, initialData]);

    const isEditing = !!initialData;
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Auto-calculate RCV if Qty or Price changes
    useEffect(() => {
        if (!isOpen || isEditing) return;
        const q = parseFloat(quantity);
        const p = parseFloat(unitPrice);
        const t = parseFloat(tax) || 0;
        const o = parseFloat(op) || 0;

        if (!isNaN(q) && !isNaN(p)) {
            const calculated = (q * p) + t + o;
            setRcv(calculated.toFixed(2));
        }
    }, [quantity, unitPrice, tax, op, isOpen, isEditing]);

    const handleSave = async () => {
        if (!description) {
            console.warn("Save failed: Description is missing");
            return;
        }
        if (!rcv) {
            console.warn("Save failed: RCV is missing");
            return;
        }

        setIsSubmitting(true);
        const payload: CreateLineItemRequest = {
            description,
            quantity: quantity ? parseFloat(quantity) : null,
            unit: unit || null,
            unitPriceCents: unitPrice ? Math.round(parseFloat(unitPrice) * 100) : null,
            taxCents: tax ? Math.round(parseFloat(tax) * 100) : null,
            opCents: op ? Math.round(parseFloat(op) * 100) : null,
            rcvCents: Math.round(parseFloat(rcv) * 100),
            lineItemNumber: lineItemNumber || null
        };

        console.log("Saving line item with payload:", payload);

        try {
            if (isEditing) {
                console.log("Updating item:", initialData.lineItemId);
                if (initialData.lineItemId == null) {
                    // Throw (rather than return) so this still flows through the
                    // catch/finally below — a bare return here would rely on the
                    // guard staying inside this exact try block to reset
                    // isSubmitting; throwing keeps that guaranteed regardless.
                    throw new Error('Cannot update line item: missing line item ID');
                }
                await updateLineItem({
                    itemId: initialData.lineItemId,
                    ...payload
                });
            } else {
                console.log("Creating item in room:", roomId);
                await createLineItem({
                    roomId,
                    ...payload
                });
            }
            onClose();
        } catch (error) {
            console.error(error);
            // Handle error (show toast?)
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Edit Line Item' : 'New Line Item'}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="lineItemNumber" className="sm:text-right">
                            #
                        </Label>
                        <Input
                            id="lineItemNumber"
                            type="text"
                            value={lineItemNumber}
                            onChange={(e) => setLineItemNumber(e.target.value)}
                            className="sm:col-span-3"
                            placeholder="Auto"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="description" className="sm:text-right">
                            Description
                        </Label>
                        <Input
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="sm:col-span-3"
                            autoFocus
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="quantity" className="sm:text-right">
                            Quantity
                        </Label>
                        <Input
                            id="quantity"
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            className="sm:col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="unit" className="sm:text-right">
                            Unit
                        </Label>
                        <Input
                            id="unit"
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            className="sm:col-span-3"
                            placeholder="EA, SF, etc."
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="unitPrice" className="sm:text-right">
                            Unit Price
                        </Label>
                        <Input
                            id="unitPrice"
                            type="number"
                            value={unitPrice}
                            onChange={(e) => setUnitPrice(e.target.value)}
                            className="sm:col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="tax" className="sm:text-right">
                            Tax
                        </Label>
                        <Input
                            id="tax"
                            type="number"
                            value={tax}
                            onChange={(e) => setTax(e.target.value)}
                            className="sm:col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="op" className="sm:text-right">
                            O&P
                        </Label>
                        <Input
                            id="op"
                            type="number"
                            value={op}
                            onChange={(e) => setOp(e.target.value)}
                            className="sm:col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 sm:items-center gap-1 sm:gap-4">
                        <Label htmlFor="rcv" className="sm:text-right font-bold">
                            RCV
                        </Label>
                        <Input
                            id="rcv"
                            type="number"
                            value={rcv}
                            onChange={(e) => setRcv(e.target.value)}
                            className="sm:col-span-3 font-bold"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={!description || !rcv}>
                        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
